import React, { useMemo } from "react";
import { type SavedPlotRow, type PlotRequestTag } from "../../types";
import {
  type GridSelectionModel,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { StyledDataGrid } from "@/components/DataGrid/ChibanKaokuNumberSearchStyleDataGrid";
import { format, parseISO } from "date-fns";
import {
  Box,
  Link,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  Pagination,
  Stack,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { TagGrid } from "../TagGrid";
import { useNavigate } from "react-router-dom";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { useIsMobile } from "@/hooks/useIsMobile";
import { ResponsiveTable } from "@/components/Responsive";
import { MIN_TAP_TARGET } from "@/styles/responsive";

interface IProps {
  handleRowSelectionChange: (newSelectionModel: GridSelectionModel) => void;
  rows: SavedPlotRow[];
  selectedIds: GridSelectionModel;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  crm: boolean;
  availableTags?: PlotRequestTag[];
  /** 特定の行の物件リストを CSV/Excel 形式でダウンロードするコールバック */
  onDownloadRow: (
    row: SavedPlotRow,
    formatType: "csv" | "excel"
  ) => Promise<void>;
}

/**
 * リスト一覧の各行に表示するダウンロードボタンコンポーネント
 * ボタンをクリックするとCSV/Excelダウンロードを選択するメニューを表示します。
 */
const RowDownloadButton: React.FC<{
  row: SavedPlotRow;
  onDownload: (row: SavedPlotRow, formatType: "csv" | "excel") => Promise<void>;
}> = ({ row, onDownload }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  /**
   * ボタンクリック時の処理
   * 行選択イベントの発生（DataGridのチェックボックス連動など）を防ぐためにイベント伝播を抑制します。
   */
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  /**
   * ダウンロードメニューを閉じる処理
   */
  const handleClose = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  /**
   * 各ダウンロードメニュー項目選択時の処理
   */
  const handleDownloadClick = async (
    event: React.MouseEvent,
    formatType: "csv" | "excel"
  ): Promise<void> => {
    event.stopPropagation(); // 行選択などの親要素のイベント発火を防止
    setAnchorEl(null);
    await onDownload(row, formatType);
  };

  return (
    <>
      <Button
        variant="text"
        size="small"
        startIcon={<FileDownloadIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleClick}
      >
        ダウンロード
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => {
          // メニュー自体のクリックが親要素に伝わるのを防ぐ
          e.stopPropagation();
        }}
      >
        <MenuItem
          onClick={(e) => {
            void handleDownloadClick(e, "csv");
          }}
        >
          CSV形式でダウンロード
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            void handleDownloadClick(e, "excel");
          }}
        >
          Excel形式でダウンロード
        </MenuItem>
      </Menu>
    </>
  );
};

/** 同じ会社の他のユーザーから共有された行が含まれるか */
export const hasSharedRow = (rows: SavedPlotRow[]): boolean =>
  rows.some((row) => row.isEditable === false);

/** 保存日時・更新日時の表示形式（ISO 8601 を解析。失敗したら元の文字列） */
const formatSavedPlotDate = (dateString: string): string => {
  try {
    return format(parseISO(dateString), "yyyy/MM/dd HH:mm:ss");
  } catch (e) {
    console.error("日付のフォーマットに失敗しました:", dateString, e);
    return dateString;
  }
};

/**
 * SP の一覧。1行=1カード（ResponsiveTable）で、選択・ページは PC の DataGrid と同じ state を使う。
 * カードには選択・識別名・件数・保存日時を出し、残りはカードの「詳細」に逃がす
 */
const SavedPlotCards: React.FC<
  Pick<
    IProps,
    | "handleRowSelectionChange"
    | "rows"
    | "selectedIds"
    | "page"
    | "setPage"
    | "pageSize"
    | "crm"
    | "onDownloadRow"
  > & { onOpenDetail: (row: SavedPlotRow) => void }
> = ({
  handleRowSelectionChange,
  rows,
  selectedIds,
  page,
  setPage,
  pageSize,
  crm,
  onDownloadRow,
  onOpenDetail,
}) => {
  // PC の DataGrid と同じく保存日時の新しい順
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [rows]
  );
  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = sortedRows.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize
  );

  const toggle = (id: number): void => {
    handleRowSelectionChange(
      selectedIds.includes(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id]
    );
  };

  const showUserName = hasSharedRow(rows);
  const headers = crm
    ? [
        "選択",
        "識別名",
        "件数",
        ...(showUserName ? ["作成者"] : []),
        "保存日時",
        "タグ",
        "更新日時",
        "物件の一覧",
        "ダウンロード",
      ]
    : [
        "選択",
        "識別名",
        "件数",
        ...(showUserName ? ["作成者"] : []),
        "保存日時",
        "ダウンロード",
      ];

  const tableRows = pageRows.map((row) => {
    const select = (
      <Checkbox
        checked={selectedIds.includes(row.id)}
        onChange={() => {
          toggle(row.id);
        }}
        inputProps={{ "aria-label": `${row.name}を選択` }}
        sx={{ p: 1.25, my: -1.25 }}
      />
    );
    const download = <RowDownloadButton row={row} onDownload={onDownloadRow} />;
    const base = [
      select,
      row.name,
      formatNumberWithComma(row.count),
      ...(showUserName ? [row.userName] : []),
      formatSavedPlotDate(row.createdAt),
    ];
    return crm
      ? [
          ...base,
          <TagGrid key="tags" tags={row.tags?.map((tag) => tag.name) ?? []} />,
          formatSavedPlotDate(row.updatedAt),
          <Button
            key="detail"
            variant="text"
            size="small"
            sx={{ minHeight: MIN_TAP_TARGET }}
            onClick={() => {
              onOpenDetail(row);
            }}
          >
            開く
          </Button>,
          download,
        ]
      : [...base, download];
  });

  return (
    <Stack spacing={1.5}>
      <ResponsiveTable
        title="保存した物件リスト一覧"
        headers={headers}
        rows={tableRows}
        summaryColumns={showUserName ? [0, 1, 2, 3, 4] : [0, 1, 2, 3]}
        getRowId={(_, i) => pageRows[i]?.id ?? i}
        emptyMessage="保存した物件リストがありません"
      />
      {pageCount > 1 && (
        <Pagination
          count={pageCount}
          // Pagination は 1 始まり、DataGrid の page は 0 始まり
          page={currentPage + 1}
          onChange={(_, nextPage) => {
            setPage(nextPage - 1);
          }}
          sx={{ alignSelf: "center" }}
        />
      )}
    </Stack>
  );
};

export const SavedPlotFeedGrid: React.FC<IProps> = ({
  handleRowSelectionChange,
  rows,
  selectedIds,
  page,
  setPage,
  pageSize,
  setPageSize,
  crm,
  availableTags = [],
  onDownloadRow,
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const openDetail = (row: SavedPlotRow): void => {
    navigate(`/mapplotrequest/${row.id}`, {
      state: {
        mapPlotRequestTagOptions: availableTags.map((tag) => tag.name),
        shouldRefresh: true,
      },
    });
  };

  if (isMobile) {
    return (
      <SavedPlotCards
        handleRowSelectionChange={handleRowSelectionChange}
        rows={rows}
        selectedIds={selectedIds}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        crm={crm}
        onDownloadRow={onDownloadRow}
        onOpenDetail={openDetail}
      />
    );
  }

  // DataGrid の内部ページネーションに合わせ、現在表示中のページの選択状態は自動的に管理されるので
  // 全体の選択状態をそのまま渡す
  const currentPageSelection = selectedIds;

  const handlePageChange = (newPage: number): void => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize);
  };

  const formatDate = formatSavedPlotDate;

  // CRM用の列定義（幅を調整してスクロールバーを回避）
  const crmBaseColumns: GridColDef[] = [
    {
      field: "name",
      headerName: "識別名",
      width: 280,
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "count",
      headerName: "件数",
      width: 100,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) =>
        formatNumberWithComma(params.value as number),
    },
  ];

  // 非CRM用の列定義（元の幅を維持）
  const nonCrmBaseColumns: GridColDef[] = [
    {
      field: "name",
      headerName: "識別名",
      width: 300,
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "count",
      headerName: "件数",
      width: 100,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) =>
        formatNumberWithComma(params.value as number),
    },
  ];

  const userNameColumn: GridColDef = {
    field: "userName",
    headerName: "作成者",
    width: 140,
    sortable: true,
    align: "center",
    headerAlign: "center",
  };
  // 共有された行が無い（自分の行だけの）ときは、作成者が全行で自分になるため列を出さない
  const userNameColumns = hasSharedRow(rows) ? [userNameColumn] : [];

  const tagsColumn: GridColDef = {
    field: "tags",
    headerName: "タグ",
    width: 200,
    sortable: false,
    align: "left",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams) => {
      const tags = params.value as PlotRequestTag[] | undefined;
      const tagNames = tags?.map((tag) => tag.name) ?? [];
      return (
        <Box sx={{ py: 1, overflow: "hidden" }}>
          <TagGrid tags={tagNames} />
        </Box>
      );
    },
  };

  const actionsColumn: GridColDef = {
    field: "actions",
    headerName: "詳細",
    width: 100,
    sortable: false,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams<SavedPlotRow>) => {
      return (
        <Link
          component="button"
          variant="body2"
          onClick={() => {
            openDetail(params.row as SavedPlotRow);
          }}
          sx={{ cursor: "pointer" }}
        >
          詳細
        </Link>
      );
    },
  };

  // 各行から直接データをエクスポートするためのダウンロードボタン列
  const downloadColumn: GridColDef = {
    field: "download",
    headerName: "ダウンロード",
    width: 160,
    sortable: false,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams<unknown, SavedPlotRow>) => {
      const row = params.row;
      return <RowDownloadButton row={row} onDownload={onDownloadRow} />;
    },
  };

  const crmCreatedAtColumn: GridColDef = {
    field: "createdAt",
    headerName: "保存日時",
    width: 160,
    sortable: true,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams) => {
      if (params.value && typeof params.value === "string") {
        return formatDate(params.value);
      }
      return "";
    },
  };

  const nonCrmCreatedAtColumn: GridColDef = {
    field: "createdAt",
    headerName: "保存日時",
    width: 170,
    sortable: true,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams) => {
      if (params.value && typeof params.value === "string") {
        return formatDate(params.value);
      }
      return "";
    },
  };

  const updatedAtColumn: GridColDef = {
    field: "updatedAt",
    headerName: "更新日時",
    width: 160,
    sortable: true,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams) => {
      if (params.value && typeof params.value === "string") {
        return formatDate(params.value);
      }
      return "";
    },
  };

  const columns: GridColDef[] = crm
    ? [
        ...crmBaseColumns,
        ...userNameColumns,
        tagsColumn,
        crmCreatedAtColumn,
        updatedAtColumn,
        actionsColumn,
        downloadColumn,
      ]
    : [
        ...nonCrmBaseColumns,
        ...userNameColumns,
        nonCrmCreatedAtColumn,
        downloadColumn,
      ];

  return (
    <StyledDataGrid
      disableColumnMenu
      // 1ページは最大100行なので全行を描く。仮想化したままだと、行の高さを自動にしているため
      // スクロールした後に行を選ぶと、描画する行の範囲が計算し直されて表示が行単位でずれることがある
      disableVirtualization
      rows={rows}
      columns={columns}
      checkboxSelection
      selectionModel={currentPageSelection}
      page={page}
      pageSize={pageSize}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      pagination
      rowsPerPageOptions={[25, 50, 100]}
      initialState={{
        sorting: {
          sortModel: [{ field: "createdAt", sort: "desc" }],
        },
        columns: {
          columnVisibilityModel: { id: false },
        },
      }}
      onSelectionModelChange={handleRowSelectionChange}
      getRowHeight={() => "auto"}
      sx={{
        "& .MuiDataGrid-cell": {
          py: 1,
        },
      }}
    />
  );
};
